import { t } from '../i18n';
import React, { useState } from 'react';
import FeeSlider from './FeeSlider';
import PropTypes from 'prop-types';

/**
 * fee_percent v6
 * - 입력/DB 값 = 바로 상위 계정에 내려온 Pool을 100으로 보는 상대 배분율(0~100%).
 * - 서버는 이 상대비율 자체를 저장하고 실제 정산 시 단계별로 곱한다.
 * - 상위 비율을 바꿔도 하위 계정의 상대비율 설정은 유지된다.
 */
export default function EditFeeModal({
  title = t('수수료 수정'),
  initialPercent = 0,
  maxPercent = 100,
  ancestorPercent = 100,
  confirmText = t('적용'),
  cancelText = t('취소'),
  onSubmit,
  onClose,
}) {
  const safeMax = Math.max(0, Math.min(100, Number(maxPercent) || 100));
  const [fee, setFee] = useState(Math.min(safeMax, Math.max(0, Number(initialPercent) || 0)));

  React.useEffect(() => {
    setFee(Math.min(safeMax, Math.max(0, Number(initialPercent) || 0)));
  }, [initialPercent, safeMax]);

  const [submitting, setSubmitting] = useState(false);
  const handleSave = async () => {
    try {
      setSubmitting(true);
      const allocationPercent = Math.min(safeMax, Math.max(0, Number(fee) || 0));
      await onSubmit(allocationPercent);
      onClose?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center overflow-y-auto bg-black/50 p-4 sm:p-6">
      <div className="w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-xl bg-white p-4 sm:p-5 shadow-xl">
        <div className="mb-3 text-lg font-semibold">{title}</div>
        <p className="mb-3 text-sm text-gray-600">
          {t('입력값은 상위 브랜치 풀 안에서 이 하위 계정에 배분할 비율입니다.')}<br />
          {t('상위 브랜치 풀')} {Number(ancestorPercent || 0).toFixed(4)}% · {t('배분율 범위')} 0~{safeMax.toFixed(2)}%
        </p>

        <FeeSlider
          value={Number(fee) || 0}
          min={0}
          max={safeMax}
          step={0.0001}
          onChange={(n) => setFee(n)}
          onChangeEnd={(n) => setFee(n)}
          label={t('상위 풀 기준 배분율(%)')}
        />

        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" disabled={submitting}>
            {cancelText}
          </button>
          <button onClick={handleSave} className="rounded bg-black px-4 py-2 text-sm text-white disabled:opacity-50" disabled={submitting}>
            {confirmText}
          </button>
        </div>

        <div className="mt-3 text-xs text-gray-400">
          {t('* 상위 계정의 비율을 변경해도 하위 계정에 저장된 상대비율은 그대로 유지됩니다.')}
        </div>
      </div>
    </div>
  );
}

EditFeeModal.propTypes = {
  title: PropTypes.string,
  initialPercent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxPercent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ancestorPercent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};
